import { useCallback, useState } from "react";

import { useTranslation } from "next-i18next";

import Icon from "@components/Icon";
import { useMessage } from "@components/Message";

import sendForgotPassword from "../services/sendForgotPassword";
import { TForgotPasswordFieldError, TForgotPasswordHookReturns } from "../types";

type TForgotPasswordField = {
  email: string;
};

const useForgotPassword = (): TForgotPasswordHookReturns => {
  const { t } = useTranslation();
  const [{ isLoading, hasSucceeded }, setForgotPasswordState] = useState({ isLoading: false, hasSucceeded: false });
  const [errors, setErrors] = useState<TForgotPasswordFieldError>();
  const setMessage = useMessage();

  const handleSubmit = useCallback(
    async ({ email }: TForgotPasswordField) => {
      try {
        setMessage({ message: "", props: {}, zone: "forgotPassword" }); // Clearing previous message
        setForgotPasswordState({ isLoading: true, hasSucceeded: false });

        const { isSuccess, fieldErrors, errorMessage } = await sendForgotPassword(email);
        if (!isSuccess) {
          setErrors(fieldErrors);
          throw new Error(errorMessage);
        }

        setForgotPasswordState({ isLoading: false, hasSucceeded: true });
      } catch (err: any) {
        setMessage({
          message: err?.message || t("password_recovery-errors-unsuccessful_password_recovery"),
          props: { variant: "error", icon: <Icon icon="warning" size="1.75rem" />, isAnimated: false, canClose: true },
          zone: "forgotPassword",
        });
        setForgotPasswordState({ isLoading: false, hasSucceeded: false });
      }
    },
    [setMessage, t, setForgotPasswordState]
  );

  return { errors, isLoading, hasSucceeded, handleSubmit };
};
export default useForgotPassword;
